import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import { authClient } from "../../Utils/Common";
import { useLogout } from "../../hooks/useLogout";
// import FeedbackPopover from "../FeedbackPopover/FeedbackPopover";
import ContactUsPopover from "../ContactUsPopover/ContactUsPopover";

import SidebarHeader from "./components/SidebarHeader";
import ChatHistoryList from "./components/ChatHistoryList";
import UserMenu from "./components/UserMenu";
import { useChatHistory } from "./hooks/useChatHistory";

import "./Sidebar.css";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isContactUsOpen, setIsContactUsOpen] = useState(false);

  // AuthContext loads the console profile (canAccessConsole, isAdmin) as soon
  // as Okta reports the sign-in, so the Console button appears without the
  // sidebar asking for it.
  const { user } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');



  // ── Resolve display name ────────────────────────────────────────────────────
  useEffect(() => {
    const storedAuth = sessionStorage.getItem("auth") || localStorage.getItem("auth");
    let name = "";

    try {
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        const profile = parsed?.user?.profile ?? {};
        if (profile.firstName && profile.lastName) {
          name = `${profile.firstName} ${profile.lastName}`;
        } else {
          name = profile.firstName?.trim?.() || profile.username?.trim?.() || '';
        }
      }
    } catch {
      /* ignore */
    }

    const resolveFromIdToken = async () => {
      try {
        const { idToken } = await authClient.tokenManager.getTokens();
        const claims: any = idToken?.claims ?? {};
        return (
          (claims.given_name &&
            claims.family_name &&
            `${claims.given_name} ${claims.family_name}`.trim()) ||
          claims.given_name?.trim?.() ||
          claims.name?.trim?.() ||
          claims.preferred_username?.trim?.() ||
          ''
        );
      } catch {
        return '';
      }
    };

    const finalize = async () => {
      if (!name) name = await resolveFromIdToken();
      const email = sessionStorage.getItem("userEmail") || "";
      if (!name && email) name = email.split("@")[0];
      if (!name && user?.userId) name = String(user.userId);
      setDisplayName(name);
    };

    void finalize();
  }, [user]);

  // ── Derive userEmail for history hook ───────────────────────────────────────
  const userEmail: string | null = (() => {
    try {
      const stored = sessionStorage.getItem("auth") || localStorage.getItem("auth");
      if (stored) return JSON.parse(stored)?.user?.profile?.email ?? null;
    } catch {
      /* ignore */
    }
    return sessionStorage.getItem("userEmail") || null;
  })();

  // ── Chat history ────────────────────────────────────────────────────────────
  const {
    sessions,
    folders,
    isLoading,
    error,
    refresh,
    toggleFolder,
    handleDeleteSession,
    handleRenameSession,
    handleDeleteFolder,
    handleRenameFolder,
    handleCreateFolder,
    handleMoveSession
  } = useChatHistory(userEmail);

  // // Active session comes from localStorage (set by Chat component on session start)
  // const activeSessionId = localStorage.getItem("chat_session_id");
  // Derive active session from the current URL — reactive and always accurate.
  // /chat/:sessionId → sessionId is the active session.
  // /chat (no param) → no active session (new chat or home screen).
  const activeSessionId = location.pathname.startsWith("/chat/")
    ? location.pathname.split("/chat/")[1]
    : null;

  // Refresh history whenever a new session is created
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("chat:new", handler);
    window.addEventListener("chat:session_created", handler);
    return () => {
      window.removeEventListener("chat:new", handler);
      window.removeEventListener("chat:session_created", handler);
    };
  }, [refresh]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  // Wrap delete so that if the user deletes the chat they're currently viewing,
  // we navigate them to a fresh new chat instead of leaving a stale screen.
  const handleDeleteSessionWithNav = async (sessionId: string) => {
    await handleDeleteSession(sessionId);
    if (sessionId === activeSessionId) {
      window.dispatchEvent(new Event("chat:new"));
      localStorage.removeItem("chat_session_id");
      localStorage.removeItem("session_id");
      navigate("/");
    }
  };

  const handleNewChat = () => {
    window.dispatchEvent(new Event("chat:new"));
    localStorage.removeItem("chat_session_id");
    localStorage.removeItem("session_id");
    navigate("/");
  };

  const handleSessionClick = (sessionId: string) => {
    // navigate(`/chat?session_id=${sessionId}`);
    navigate(`/chat/${sessionId}`);
  };

  const handleLogout = useLogout();

  const handleNavigateHelp = () =>
    window.open("/help", "_blank", "noopener,noreferrer");


  const isAdmin = !!user?.isAdmin;
  const canAccessConsole = !!user?.canAccessConsole;
  const isChatActive = location.pathname === '/chat';
  // const isFeedbackActive = location.pathname === "/feedback";
  const isContactUsActive = location.pathname === "/contact-us";
  const isConsoleActive = location.pathname.startsWith('/console');

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <SidebarHeader
        isCollapsed={isCollapsed}
        onToggle={onToggle}
        onNewChat={handleNewChat}
        onCreateFolder={handleCreateFolder}
      />

      {!isCollapsed && (
        <div className="sidebar-content">
          <ChatHistoryList
            sessions={sessions}
            folders={folders}
            isLoading={isLoading}
            error={error}
            activeSessionId={activeSessionId}
            onSessionClick={handleSessionClick}
            onSessionRename={handleRenameSession}
            onSessionDelete={handleDeleteSessionWithNav}
            onFolderToggle={toggleFolder}
            onFolderRename={handleRenameFolder}
            onFolderDelete={handleDeleteFolder}
            onCreateFolder={handleCreateFolder}
            onMoveSession={handleMoveSession}  // Move chat session to and from folders
          />
        </div>
      )}

      <div className="sidebar-footer">
        {/* Console button — visible to anyone where can_access_console=true from /console/me */}
        {canAccessConsole && (
          <div
            className={`chat-item ${isConsoleActive ? 'active' : ''}`}
            onClick={() => navigate(isAdmin ? '/console/admin' : '/console/dashboard')}
            role="button"
            aria-label="Open console"
            title="Console"
          >
            <i className="fas fa-terminal"></i>
            <span>Console</span>
          </div>
        )}
        <div
          className="chat-item"
          onClick={() => window.open("/help?tab=prompts", "_blank", "noopener,noreferrer")}
          role="button"
          aria-label="Open prompt Library"
          title="Prompt Library"
        >
          <i className="fas fa-book" />
          {!isCollapsed && <span>Prompt Library</span>}
        </div>

        {/* Feedback replaced by Contact Us — kept for reference, do not delete.
        <div
          className={`chat-item ${isFeedbackActive ? 'active' : ''}`}
          onClick={() => setIsFeedbackOpen(true)}
          role="button"
          aria-label="Open feedback"
          title="Feedback"
        >
          <i className="fa-solid fa-circle-exclamation" />
          {!isCollapsed && <span>Feedback</span>}
        </div>
        */}

        <div
          className={`chat-item ${isContactUsActive ? 'active' : ''}`}
          onClick={() => setIsContactUsOpen(true)}
          role="button"
          aria-label="Open contact us"
          title="Contact Us"
        >
          <i className="fa-solid fa-envelope" />
          {!isCollapsed && <span>Contact Us</span>}
        </div>

        <div
          className="chat-item"
          onClick={handleNavigateHelp}
          role="button"
          aria-label="Open help center"
          title="Help center"
        >
          <i className="fas fa-question-circle" />
          {!isCollapsed && <span>Help center</span>}
        </div>

        {/* Feedback replaced by Contact Us — kept for reference, do not delete.
        <FeedbackPopover open={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
        */}
        <ContactUsPopover open={isContactUsOpen} onClose={() => setIsContactUsOpen(false)} />

        <UserMenu
          displayName={displayName}
          isCollapsed={isCollapsed}
          onLogout={handleLogout}
        />
      </div>
    </div>
  );
};

export default Sidebar;